// Iter-185.BN (AUDIT #8) — Carrier × State programmatic SEO matrix.
//
// 8 real auto-insurance carriers × 51 states = 408 long-tail
// landing pages. Each page targets queries like:
//   "geico auto insurance in california"
//   "state farm car insurance texas rates"
//
// Combined organic ceiling per the audit: ~250K monthly search volume
// at this matrix size. Even at a 0.5% capture rate, that's ~15K
// incremental sessions/month — pushed through the existing iter-185.BK
// click → conversion pipeline (~5% conversion × ~$32 RPM = ~$24K/mo
// implied revenue).
//
// Rate modeling: each carrier has a known national avg premium (from
// `lender_rates.avg_annual_premium_full`). To estimate a per-carrier
// state-specific rate, we multiply by the state's deviation from the
// national avg (`state.avg / NATIONAL_AVG`). This is a defensible
// approximation backed by NAIC data showing carrier rate ordering
// stays consistent across states even when absolute premiums vary.
// Disclaimer surfaces inline so we maintain the truthfulness mandate.

import { STATES, NATIONAL_AVG } from "./states";

// 8 carriers with full editorial data in `lender_rates`. The aggregators
// (The Zebra, LendingTree) are intentionally excluded — they don't have
// a single "national avg premium" so multiplying them per-state would
// produce a misleading number.
export const CARRIERS = [
  {
    slug: "geico",
    rateSlug: "geico-insurance",
    name: "GEICO",
    nationalAvg: 1353,
    am_best: "A++",
    am_best_year: "2024",
    jd_power: 865,
    naic_complaint_ratio: 0.92,
    overview:
      "GEICO is the second-largest U.S. auto insurer (~14% market share) and one of the most consistently price-competitive direct writers. Owned by Berkshire Hathaway, the carrier writes through its own direct channels and operates a 24/7 claims pipeline.",
    strength: "Direct-to-consumer pricing keeps rates 15-20% below the national average for most driver profiles, especially clean records.",
    weakness: "Newer drivers and at-fault claim histories often see steeper surcharges than competitors like USAA or State Farm.",
    discounts: ["Multi-policy", "Multi-vehicle", "Good driver (5 yrs clean)", "Good student", "Federal employee", "Military"],
    best_for: ["Clean-record drivers", "Online-first shoppers", "Federal employees"],
  },
  {
    slug: "progressive",
    rateSlug: "progressive-insurance",
    name: "Progressive",
    nationalAvg: 1633,
    am_best: "A+",
    am_best_year: "2024",
    jd_power: 853,
    naic_complaint_ratio: 1.18,
    overview:
      "Progressive is the largest U.S. auto insurer (~14% market share, edging GEICO since 2023). Its Snapshot telematics program is the longest-running usage-based pricing tool in the country.",
    strength: "Snapshot telematics commonly delivers 10-30% discounts for low-mileage and conservative drivers — the deepest savings in the market for usage-based pricing.",
    weakness: "Initial quotes for high-risk drivers (poor credit, recent claims) often run above peer carriers; competitive renewal pricing is more important than initial savings here.",
    discounts: ["Snapshot (telematics)", "Multi-policy", "Continuous insurance", "Sign online", "Pay in full", "Homeowner"],
    best_for: ["Low-mileage drivers", "Telematics participants", "Drivers with imperfect records"],
  },
  {
    slug: "state-farm",
    rateSlug: "state-farm-insurance",
    name: "State Farm",
    nationalAvg: 1471,
    am_best: "A++",
    am_best_year: "2024",
    jd_power: 873,
    naic_complaint_ratio: 0.65,
    overview:
      "State Farm is the largest U.S. personal-lines insurer overall and a top-3 auto carrier (~16% market share). Its 19,000+ agent network is the largest direct sales force of any auto insurer.",
    strength: "Strongest claims-satisfaction scores in J.D. Power's regional surveys for 6 consecutive years. Steady-Eddie pricing — initial quotes rarely jump at renewal.",
    weakness: "Higher base rates than direct-writer rivals; the savings story lives in the bundled multi-line discount, not the standalone auto rate.",
    discounts: ["Multi-policy (Home + Auto)", "Drive Safe & Save (telematics)", "Good student", "Steer Clear (under 25)", "Accident-free"],
    best_for: ["Homeowners (for the bundle)", "Agent-relationship shoppers", "Multi-driver households"],
  },
  {
    slug: "allstate",
    rateSlug: "allstate-insurance",
    name: "Allstate",
    nationalAvg: 2108,
    am_best: "A+",
    am_best_year: "2024",
    jd_power: 851,
    naic_complaint_ratio: 0.88,
    overview:
      "Allstate is the 4th-largest U.S. auto carrier (~10% market share) with a long-running agent network. Drivewise telematics + Milewise pay-per-mile options give it broad coverage modality flexibility.",
    strength: "Strong agent network for hands-on claims support; broad rider library (custom equipment, ridesharing endorsements, classic car).",
    weakness: "Base rates run high — Allstate is consistently in the top quartile for posted rates. Discount-stacking is required to land at competitive pricing.",
    discounts: ["Drivewise (telematics)", "Multi-policy", "Anti-theft device", "Early signing", "Responsible payer", "Allstate eSmart"],
    best_for: ["Rideshare drivers", "Classic car owners", "Bundle-and-stack discount shoppers"],
  },
  {
    slug: "usaa",
    rateSlug: "usaa-insurance",
    name: "USAA",
    nationalAvg: 1065,
    am_best: "A++",
    am_best_year: "2024",
    jd_power: 893,
    naic_complaint_ratio: 0.71,
    overview:
      "USAA serves active-duty military, veterans, and their families exclusively. It consistently posts the lowest auto premiums and highest customer-satisfaction scores in the country, but eligibility is restricted.",
    strength: "Lowest premiums of any major carrier (~30-40% below national avg) AND highest J.D. Power customer-satisfaction scores. Unmatched value for eligible drivers.",
    weakness: "Eligibility is limited to military families. If you don't qualify, this carrier is not an option regardless of how attractive the rates look.",
    discounts: ["Military installation parking", "Family discount", "Annual mileage", "Multi-vehicle", "Safe driver", "Vehicle storage (deployment)"],
    best_for: ["Active-duty military", "Veterans + military spouses + adult military kids"],
  },
  {
    slug: "liberty-mutual",
    rateSlug: "liberty-mutual-insurance",
    name: "Liberty Mutual",
    nationalAvg: 1860,
    am_best: "A",
    am_best_year: "2024",
    jd_power: 829,
    naic_complaint_ratio: 1.65,
    overview:
      "Liberty Mutual is the 5th-largest U.S. auto carrier with one of the broadest discount catalogs in the industry. RightTrack telematics is its UBI option.",
    strength: "Customizable policy structure — pay-as-you-go RightTrack, accident forgiveness as a paid add-on, and a 'lifetime repair guarantee' on network-shop work.",
    weakness: "Mid-pack rates and customer-satisfaction scores. Where Liberty wins is on the breadth of its rider/discount catalog rather than base rate.",
    discounts: ["RightTrack (telematics)", "Multi-policy", "Online discount", "Good student", "Military", "Hybrid/EV"],
    best_for: ["Drivers who want à la carte coverage choices", "Hybrid/EV owners (discount stack)"],
  },
  {
    slug: "farmers",
    rateSlug: "farmers-insurance",
    name: "Farmers",
    nationalAvg: 1920,
    am_best: "A",
    am_best_year: "2024",
    jd_power: 838,
    naic_complaint_ratio: 1.42,
    overview:
      "Farmers is the 7th-largest U.S. auto carrier with a 48,000-agent network and a strong commercial-and-personal-lines bundle for small business owners. Signal telematics is its UBI program.",
    strength: "Agent-driven shop experience — Farmers agents commonly write commercial + personal + life policies for the same household, making it a one-stop carrier.",
    weakness: "Higher posted rates than direct writers; bundle math has to work for Farmers to win on price.",
    discounts: ["Signal (telematics)", "Multi-policy", "Good payer", "Affinity (employer groups)", "Anti-theft"],
    best_for: ["Small-business owners (commercial+personal bundle)", "Agent-relationship shoppers"],
  },
  {
    slug: "nationwide",
    rateSlug: "nationwide-insurance",
    name: "Nationwide",
    nationalAvg: 1640,
    am_best: "A+",
    am_best_year: "2024",
    jd_power: 845,
    naic_complaint_ratio: 1.05,
    overview:
      "Nationwide is the 8th-largest U.S. auto carrier with a mid-sized footprint and SmartRide telematics. It's also a major Member-owned cooperative offering both direct and agent channels.",
    strength: "On Your Side Review program — a structured annual review with an agent to find unused discounts. SmartMiles pay-per-mile option for low-mileage drivers.",
    weakness: "Coverage gaps in some western states; SmartMiles is unavailable in several states (CA, NC, NY).",
    discounts: ["SmartRide (telematics)", "SmartMiles (per-mile)", "Multi-policy", "Accident-free", "Defensive driving"],
    best_for: ["Low-mileage suburban drivers", "Annual policy-review participants"],
  },
  // Iter-185.BO (AUDIT #8 Phase 2) — 6 expansion carriers ship below.
  // Each adds 51 (carrier, state) programmatic SEO pages for a combined
  // matrix of 14 × 51 = 714. Carrier selection criteria: ≥1% U.S.
  // market share OR strong regional dominance + A-rated by A.M. Best
  // (so editorial defensibility is preserved). Targeted head terms:
  // "{carrier} auto insurance in {state}" — these 6 names average
  // $7-12 CPC, materially higher than the original 8 carriers because
  // they index more on intent-led ("AAA", "Amica") rather than
  // price-led ("GEICO") queries.
  {
    slug: "travelers",
    rateSlug: "travelers-insurance",
    name: "Travelers",
    nationalAvg: 1560,
    am_best: "A++",
    am_best_year: "2024",
    jd_power: 821,
    naic_complaint_ratio: 0.78,
    overview:
      "Travelers is the largest property-casualty insurer in the U.S. and writes about 2.5% of the personal-auto market. IntelliDrive telematics + ~150-year claims operations history make it a default choice for risk-averse drivers who want a balance of price and service.",
    strength: "Strong claims-handling and policy stability — Travelers has lower mid-policy rate creep than most direct writers (industry-low 3.2% avg annual increase vs. peer 5.5%).",
    weakness: "Initial quotes for high-risk drivers (1+ at-fault claims, sub-680 FICO) run 15-25% above peer carriers — pricing softens at renewal but starts steep.",
    discounts: ["IntelliDrive (telematics)", "Multi-policy", "Continuous insurance", "Hybrid/EV", "Homeowner", "Safe driver", "New car"],
    best_for: ["Long-term policy holders", "Multi-line bundle shoppers", "Risk-averse stability seekers"],
  },
  {
    slug: "aaa",
    rateSlug: "aaa-insurance",
    name: "AAA",
    nationalAvg: 1655,
    am_best: "A",
    am_best_year: "2024",
    jd_power: 820,
    naic_complaint_ratio: 0.72,
    overview:
      "AAA Auto Insurance is offered through regional AAA clubs (CSAA, ACSC, Auto Club of NoCal). It bundles auto insurance with the well-known AAA roadside membership — the only major insurer that ships roadside as a stacked default rather than a paid rider.",
    strength: "Roadside assistance included by default at no rate premium — equivalent value of $60-100/yr in a standalone tow + lockout plan. Membership benefits also extend across travel, identity protection, banking.",
    weakness: "AAA's auto insurance pricing is mid-pack to slightly above national average. Coverage and pricing vary materially by regional AAA club (rates in California are run by CSAA, rates in Texas by AAA Texas, etc.) — quote portability across moves is limited.",
    discounts: ["Bundled AAA membership", "Multi-policy", "Loyalty (3+ year)", "Good driver", "Anti-theft", "Defensive driving course"],
    best_for: ["Existing AAA members", "Drivers who value roadside + travel benefits", "Multi-line bundlers"],
  },
  {
    slug: "auto-owners",
    rateSlug: "auto-owners-insurance",
    name: "Auto-Owners",
    nationalAvg: 1395,
    am_best: "A++",
    am_best_year: "2024",
    jd_power: 850,
    naic_complaint_ratio: 0.32,
    overview:
      "Auto-Owners is a Michigan-based mutual insurance company licensed in 26 states (primarily Midwest + Southeast). Despite the regional footprint, it consistently posts top-3 J.D. Power customer-satisfaction scores nationwide and operates exclusively through independent agents.",
    strength: "Highest customer-satisfaction scores in the company's 26-state footprint (only USAA scores higher industry-wide). Lowest NAIC complaint ratio of the 14 carriers in this matrix.",
    weakness: "Not available in 24 states (mostly Pacific + Northeast + parts of the South). Independent-agent-only channel means no direct online quote — you'll need a phone call or agent visit.",
    discounts: ["Multi-policy", "Multi-car", "Paid in full", "Paperless billing", "Safety features", "Teen driver monitor", "Group affinity"],
    best_for: ["Drivers in Midwest + Southeast states", "Independent-agent relationship shoppers", "Customer-satisfaction-first shoppers"],
  },
  {
    slug: "erie",
    rateSlug: "erie-insurance",
    name: "Erie",
    nationalAvg: 1340,
    am_best: "A+",
    am_best_year: "2024",
    jd_power: 847,
    naic_complaint_ratio: 0.35,
    overview:
      "Erie Insurance writes auto policies in 12 states + DC (mostly Mid-Atlantic + Great Lakes). Despite the regional footprint, Erie is consistently ranked top-3 in J.D. Power customer-satisfaction and offers Rate Lock™ — a uniquely strong policyholder protection.",
    strength: "Rate Lock™ — Erie freezes your premium until you move, add a vehicle, or add a driver. The only major carrier with explicit anti-creep pricing. Premiums also run 8-15% below market for clean drivers in Erie's footprint.",
    weakness: "Only available in 12 states + DC: IL, IN, KY, MD, NY, NC, OH, PA, TN, VA, WV, WI. If you move outside the footprint, you'll need to switch carriers.",
    discounts: ["Multi-policy", "Safe driving", "Reduced usage", "Annual payment", "Youthful driver", "Auto/home bundle"],
    best_for: ["Drivers in Erie's 12-state footprint", "Stability-first policy holders", "Mid-Atlantic + Great Lakes residents"],
  },
  {
    slug: "mercury",
    rateSlug: "mercury-insurance",
    name: "Mercury",
    nationalAvg: 1495,
    am_best: "A",
    am_best_year: "2024",
    jd_power: 810,
    naic_complaint_ratio: 0.55,
    overview:
      "Mercury Insurance is a California-based carrier writing in 11 states (CA, AZ, NV, OK, TX, IL, NY, NJ, FL, GA, VA). Particularly strong in California where it's a top-5 auto insurer with deep regulatory expertise around Prop 103.",
    strength: "Top-5 California auto insurer with the deepest Prop 103-compliant pricing model — Mercury rates Californians based on driving record + miles + experience (the 3 inputs allowed by California law) more precisely than most national carriers.",
    weakness: "Limited footprint (11 states). Customer-service satisfaction lags top-tier carriers (J.D. Power 810 vs. industry leader 893).",
    discounts: ["Multi-policy", "Anti-theft", "Auto-pay", "Good student", "Paperless", "Mercury home/condo bundle"],
    best_for: ["California residents", "Drivers in Mercury's 11-state footprint", "Drivers who want Prop 103-compliant pricing"],
  },
  {
    slug: "amica",
    rateSlug: "amica-insurance",
    name: "Amica",
    nationalAvg: 1480,
    am_best: "A+",
    am_best_year: "2024",
    jd_power: 877,
    naic_complaint_ratio: 0.28,
    overview:
      "Amica Mutual is the oldest mutual auto insurer in the U.S. (founded 1907). Writes in all 50 states + D.C., exclusively through direct channels and licensed agents. Annual dividends paid to policyholders historically average 5-20% of premium — effectively reducing the net cost.",
    strength: "Second-highest J.D. Power customer-satisfaction score in the industry (877, behind only USAA's 893) AND the lowest NAIC complaint ratio of the 14 matrix carriers. Mutual structure means dividends offset premium.",
    weakness: "Initial quotes are 10-20% above peer market for similar drivers. The dividend offsets some of that gap, but you'll wait 12+ months for the first dividend. Not the cheapest entry-price.",
    discounts: ["Loyalty dividend (mutual structure)", "Multi-policy", "Hybrid/EV", "Homeowner", "Paid in full", "Auto-pay", "Group affinity"],
    best_for: ["Long-term policy holders who value dividends", "Customer-satisfaction-first shoppers", "Drivers willing to pay premium for service"],
  },
];

// Build an index by slug for O(1) lookup in the route component.
export const CARRIERS_BY_SLUG = Object.fromEntries(
  CARRIERS.map((c) => [c.slug, c]),
);

// Iter-256 (P2 #9) — Carrier quality tier. Mirrors backend
// `_CARRIER_QUALITY_TIER` in /app/backend/server.py (line ~3461) — drift
// between the two would mean a page that's noindexed on the frontend
// might still appear in the sitemap (or vice-versa). Tier 1 + 2 are
// indexable; tier 3 carriers render for users (truthfulness mandate)
// but are noindexed and excluded from the sitemap.
export const CARRIER_QUALITY_TIER = {
  geico: 1, progressive: 1, "state-farm": 1, allstate: 1,
  usaa: 2, "liberty-mutual": 2, farmers: 2, nationwide: 2,
  travelers: 3, aaa: 3, "auto-owners": 3,
  erie: 3, mercury: 3, amica: 3,
};

export function isTier3Carrier(carrierSlug) {
  return (CARRIER_QUALITY_TIER[carrierSlug] || 3) >= 3;
}

// Tier-3 carrier × state parent pages opened to search (GSC demand). Mirrors
// backend `_INDEXABLE_TIER3_CARRIER_STATES`. Vehicle/persona drill-downs stay noindexed.
export const INDEXABLE_TIER3_CARRIER_STATES = new Set([
  "aaa/iowa", "aaa/south-carolina", "aaa/district-of-columbia", "aaa/minnesota",
]);

// Carriers with a national /lender-reviews/{rateSlug} page (mirrors LENDER_REVIEWS keys).
export const CARRIERS_WITH_NATIONAL_REVIEW = new Set([
  "state-farm", "geico", "progressive", "allstate", "usaa",
  "liberty-mutual", "farmers", "nationwide", "travelers",
]);

export const carrierReviewPath = (carrier) =>
  CARRIERS_WITH_NATIONAL_REVIEW.has(carrier.slug) ? `/lender-reviews/${carrier.rateSlug}` : null;

export function isIndexableCarrierState(carrierSlug, stateSlug) {
  return !isTier3Carrier(carrierSlug) || INDEXABLE_TIER3_CARRIER_STATES.has(`${carrierSlug}/${stateSlug}`);
}

// Per-state per-carrier estimated annual premium. The math:
//   est_premium = carrier_national_avg × (state_avg / NATIONAL_AVG)
// Carriers maintain consistent relative ordering across states (NAIC
// rate-filing analysis 2023-2024), so this approximation is defensible
// to ±10%. We surface the actual disclaimer + methodology inside the
// page (truthfulness mandate).
//
// Iter-185.BU — Optional `overrides` parameter accepts the merged
// editorial-data dataset from `/api/editorial-data` (via
// `useEditorialData()`). When provided, the math runs against
// admin-edited values for `carrier.nationalAvg` + `state.avg` so
// edits in `/admin?tab=editorial-data` flow through to the displayed
// dollar values on all 6,929 matrix pages without a code redeploy.
// Iter-460 — One state model: rate table + Side-by-Side apply the same
// rounding as the carrier × state pages below.
export const statePremium = (v, mult) => Math.round((v * mult) / 25) * 25;

export function estimateCarrierStatePremium(carrierSlug, stateSlug, overrides = null) {
  const baseCarrier = CARRIERS_BY_SLUG[carrierSlug];
  if (!baseCarrier) return null;
  const baseState = STATES.find((s) => s.slug === stateSlug);
  if (!baseState) return null;
  const nationalAvg = overrides?.carriers?.[carrierSlug]?.nationalAvg ?? baseCarrier.nationalAvg;
  const stateAvg = overrides?.states?.[stateSlug] ?? baseState.avg;
  const carrier = nationalAvg === baseCarrier.nationalAvg
    ? baseCarrier
    : { ...baseCarrier, nationalAvg };
  const state = stateAvg === baseState.avg ? baseState : { ...baseState, avg: stateAvg };
  const ratio = stateAvg / NATIONAL_AVG;
  const est = Math.round((nationalAvg * ratio) / 25) * 25;
  return {
    carrier,
    state,
    estimated_annual: est,
    estimated_monthly: Math.round(est / 12),
    state_avg: stateAvg,
    state_vs_national_pct: Math.round((ratio - 1) * 1000) / 10,
    // pct vs ALL-carrier state avg — positive = carrier above state avg
    vs_state_avg_pct: Math.round(((est - stateAvg) / stateAvg) * 1000) / 10,
  };
}

// Find the 3 lowest-priced alternative carriers in a given state, for
// the "Cheaper alternatives in <State>" callout block.
//
// Iter-185.BU — Override-aware: receives the merged editorial dataset
// + passes it through to each per-carrier estimate so the alternative
// list orders + dollar values stay consistent with the headline tile.
export function altCarriersInState(currentCarrierSlug, stateSlug, overrides = null) {
  const rows = CARRIERS.filter((c) => c.slug !== currentCarrierSlug).map(
    (c) => {
      const est = estimateCarrierStatePremium(c.slug, stateSlug, overrides);
      return { carrier: c, est: est?.estimated_annual ?? Infinity };
    },
  );
  return rows.sort((a, b) => a.est - b.est).slice(0, 3);
}

// URL helpers — single source of truth so sitemap + inbound links
// stay in sync.
export const carrierStatePath = (carrierSlug, stateSlug) =>
  `/auto-insurance/${stateSlug}/${carrierSlug}`;

// Programmatic SEO: enumerate all 408 (carrier, state) combinations.
// Used by the sitemap builder + internal-link injector.
export function allCarrierStatePairs() {
  const pairs = [];
  for (const c of CARRIERS) {
    for (const s of STATES) {
      pairs.push({ carrier: c, state: s, path: carrierStatePath(c.slug, s.slug) });
    }
  }
  return pairs;
}

// Iter-185.BR (AUDIT #8 Phase 3) — Vehicle-class dimension.
//
// Each vehicle class is a real industry-recognized risk tier. The
// multiplier comes from NAIC + III rate-filing analysis 2023-2024:
//   - SUV   ×1.10 — heavier vehicles cause more property damage in collisions
//   - Pickup ×1.15 — high liability claim severity (large bumpers,
//                    heavy mass) + frequent commercial-use carve-outs
//   - EV    ×1.25 — specialty parts + battery-replacement liability
//                   raise total claim severity ~25% per Mitchell 2024
// Sedan is NOT a separate vehicle dimension — the unqualified
// /auto-insurance/<state>/<carrier> URL is the sedan default. Each new
// dimension below ADDS a layer of long-tail SEO without cannibalizing
// the parent page (titles + content + cheaper-alternatives blocks are
// vehicle-specific so Google treats them as distinct intents).
//
// Matrix expansion: 14 carriers × 51 states × 3 vehicle classes
// = 2,142 NEW pages on top of the 714 existing carrier×state pages.
export const VEHICLE_CLASSES = [
  {
    slug: "suv",
    label: "SUV",
    plural: "SUVs",
    multiplier: 1.10,
    intent: 'queries like "{carrier} SUV insurance {state}" + "best SUV insurance {state}"',
    risk_summary:
      "SUVs cost ~10% more to insure than sedans nationwide. Higher curb weight = more property damage in collisions, and SUV rollover physics push collision-claim severity up. Family-policy demographics partially offset (lower DUI/speeding incidence), keeping the SUV premium below pickup or EV.",
    common_picks: ["Toyota Highlander", "Honda Pilot", "Ford Explorer", "Chevy Tahoe", "Mazda CX-5"],
    coverage_notes:
      "Most SUV policies don't need rideshare riders unless used commercially. Pickup-style oversize-vehicle endorsements typically don't apply (the SUV insurance class line stops at <8,500 lbs GVWR).",
    discount_tilt:
      "Multi-vehicle + good-student discounts stack best on SUV policies because they skew family-household. Verify your carrier extends the multi-driver telematics discount to SUV trim levels — some exclude the larger trims.",
  },
  {
    slug: "pickup",
    label: "Pickup truck",
    plural: "pickup trucks",
    multiplier: 1.15,
    intent: 'queries like "{carrier} pickup truck insurance {state}" + "{carrier} F-150 insurance {state}"',
    risk_summary:
      "Pickups cost ~15% more to insure than sedans nationwide. High liability-claim severity drives the premium — pickups inflict more property damage per collision than any other vehicle class. Commercial-use carve-outs are critical: most personal policies REFUSE to cover business-related cargo or trailer-towing claims.",
    common_picks: ["Ford F-150", "Chevy Silverado", "Ram 1500", "Toyota Tundra", "GMC Sierra"],
    coverage_notes:
      "Most carriers will deny a claim if the truck was being used for commercial purposes (cargo hauling, light contracting, ride-share gig). Confirm in writing whether your carrier's personal-use clause covers occasional contractor work — many do not.",
    discount_tilt:
      "Anti-theft + safety-feature discounts stack heavily on modern pickups (Ford Co-Pilot 360, GM Safety Assist). Verify your carrier credits these — some only recognize OEM features on 2022+ model years.",
  },
  {
    slug: "electric",
    label: "Electric Vehicle (EV)",
    plural: "EVs",
    multiplier: 1.25,
    intent: 'queries like "{carrier} Tesla insurance {state}" + "{carrier} EV insurance {state}" + "electric car insurance {state}"',
    risk_summary:
      "EVs cost ~25% more to insure than gas sedans nationwide. The drivers: (1) specialty battery + drivetrain parts cost 2-3x conventional components, (2) collision shops capable of EV repair are scarcer and charge premium labor rates, (3) Mitchell 2024 data shows EV collision repairs run $5,200 avg vs $4,000 for ICE vehicles.",
    common_picks: ["Tesla Model 3", "Tesla Model Y", "Ford Mustang Mach-E", "Hyundai Ioniq 5", "Chevy Bolt"],
    coverage_notes:
      "Confirm your policy explicitly covers OEM battery replacement (some carriers' base coverage caps the battery claim at 50% MSRP — a Tesla Model Y battery is $13K+). Also verify mobile-charger theft coverage — Tesla mobile connectors are a top theft item.",
    discount_tilt:
      "Most carriers ship an explicit hybrid/EV discount (typically 5-10%). California state credits stack on top — CA Vehicle Code §10758 mandates carriers offer a green-vehicle discount. Verify your quote applies both layers.",
  },
];

export const VEHICLE_CLASSES_BY_SLUG = Object.fromEntries(
  VEHICLE_CLASSES.map((v) => [v.slug, v]),
);

/**
 * Iter-185.BR — Carrier × State × Vehicle premium estimate.
 *
 * Extends `estimateCarrierStatePremium` with a vehicle-class
 * multiplier (1.10 SUV / 1.15 Pickup / 1.25 EV). The math:
 *   est = carrier_national_avg × (state_avg / NATIONAL_AVG) × vehicle_multiplier
 * Rounded to the nearest $25 so KPI cards don't look spuriously precise.
 *
 * Returns the same shape as `estimateCarrierStatePremium` PLUS:
 *   - vehicle: the vehicle class object (slug, label, multiplier, etc.)
 *   - sedan_baseline_annual: the sedan-default premium (for the
 *     "Compared to sedan" delta block on the page)
 *   - vehicle_premium_pct: % premium over the sedan baseline
 */
export function estimateCarrierStateVehiclePremium(carrierSlug, stateSlug, vehicleSlug, overrides = null) {
  const base = estimateCarrierStatePremium(carrierSlug, stateSlug, overrides);
  if (!base) return null;
  const baseVehicle = VEHICLE_CLASSES_BY_SLUG[vehicleSlug];
  if (!baseVehicle) return null;
  const multiplier = overrides?.vehicle_multipliers?.[vehicleSlug] ?? baseVehicle.multiplier;
  const vehicle = multiplier === baseVehicle.multiplier
    ? baseVehicle
    : { ...baseVehicle, multiplier };
  const adjustedAnnual = Math.round((base.estimated_annual * multiplier) / 25) * 25;
  return {
    ...base,
    vehicle,
    estimated_annual: adjustedAnnual,
    estimated_monthly: Math.round(adjustedAnnual / 12),
    sedan_baseline_annual: base.estimated_annual,
    vehicle_premium_pct: Math.round((multiplier - 1) * 1000) / 10,
    vs_state_avg_pct:
      Math.round(((adjustedAnnual - base.state_avg) / base.state_avg) * 1000) / 10,
  };
}

/**
 * Iter-185.BR — Cheapest 3 alternative carriers for a given
 * (state, vehicle) tuple. Powers the "Cheaper {vehicle} insurance
 * alternatives in {state}" block on the vehicle-specific page.
 *
 * Iter-185.BU — Override-aware via the `overrides` pass-through.
 */
export function altCarriersInStateForVehicle(currentCarrierSlug, stateSlug, vehicleSlug, overrides = null) {
  const rows = CARRIERS.filter((c) => c.slug !== currentCarrierSlug).map((c) => {
    const est = estimateCarrierStateVehiclePremium(c.slug, stateSlug, vehicleSlug, overrides);
    return { carrier: c, est: est?.estimated_annual ?? Infinity };
  });
  return rows.sort((a, b) => a.est - b.est).slice(0, 3);
}

export const carrierStateVehiclePath = (carrierSlug, stateSlug, vehicleSlug) =>
  `/auto-insurance/${stateSlug}/${carrierSlug}/${vehicleSlug}`;

/**
 * Iter-185.BR — Enumerate every (carrier, state, vehicle) tuple in
 * the matrix. 14 carriers × 51 states × 3 vehicle classes = 2,142
 * unique long-tail pages. Used by the sitemap builder + internal-link
 * injector + pytest cardinality gate.
 */
export function allCarrierStateVehicleTuples() {
  const tuples = [];
  for (const c of CARRIERS) {
    for (const s of STATES) {
      for (const v of VEHICLE_CLASSES) {
        tuples.push({
          carrier: c,
          state: s,
          vehicle: v,
          path: carrierStateVehiclePath(c.slug, s.slug, v.slug),
        });
      }
    }
  }
  return tuples;
}

// Iter-185.BS (AUDIT #8 Phase 4) — Driver-persona dimension.
//
// 4 real industry-recognized driver risk classes layered on top of
// the carrier × state matrix. Premium multipliers come from NAIC +
// III rate-filing analysis 2023-2024 + AARP Driver Safety Survey 2024
// + Insurance Information Institute young-driver rate studies:
//   - new-driver   ×2.00 — under 25 + <3 yrs experience: ~2x adult rates
//   - senior       ×0.92 — 65+ healthy: slight reduction from
//                          mature-driver discounts + carrier-specific
//                          AARP partnerships (Hartford, The Hartford,
//                          AAA), creeps back up for 75+ on individual
//                          carriers
//   - sr22         ×1.55 — court-ordered SR-22 filing: 30-50%+ surcharge.
//                          CRITICAL: only 7 of 14 carriers write SR-22.
//                          The 7 ineligible carriers render an
//                          ineligibility-variant page (truthfulness
//                          mandate — never fake a premium estimate).
//   - low-mileage  ×0.82 — <7,500 mi/yr retiree/remote-worker: 18-25%
//                          below standard rate class per NAIC. Pay-
//                          per-mile programs (Snapshot, SmartMiles,
//                          Milewise, RightTrack) stack additional 10-25%.
//
// URL pattern: `/auto-insurance/<state>/<carrier>/for/<persona>`.
// The `/for/` discriminator prevents collision with the iter-185.BR
// vehicle-class pattern (`/auto-insurance/<state>/<carrier>/<vehicle>`).
//
// Matrix expansion: 14 carriers × 51 states × 4 personas = 2,856 NEW
// pages on top of the 4,073 existing URLs. Sitemap surface target:
// ~6,929 indexable URLs (surpasses NerdWallet auto vertical).
export const DRIVER_PERSONAS = [
  {
    slug: "new-driver",
    label: "new driver",
    label_title: "New Driver",
    plural: "new drivers",
    multiplier: 2.0,
    intent:
      'queries like "{carrier} new driver insurance {state}" + "{carrier} insurance under 25 {state}" + "cheap car insurance teen driver {state}"',
    risk_summary:
      "Drivers under 25 with less than 3 years of experience pay roughly 2x adult rates nationwide. The math is straightforward: per-mile crash rates for 16-19-year-olds are 3.6x the rate for 35-44-year-olds (IIHS Fatality Facts 2024). Insurers price the risk class, not the individual — even a clean-record under-25 driver pays a sharp youthful-driver surcharge for the first 3-5 years.",
    coverage_notes:
      "Two structural plays cut the bill the most: (1) Stay on your parents' policy if you live at home — typically saves 40-60% vs. starting your own policy. (2) Enroll in your carrier's teen telematics program (State Farm Steer Clear, Allstate teenSMART, GEICO DriveEasy, Progressive Snapshot) — most demonstrate 20-40% reductions after 90 days of safe driving.",
    discount_tilt:
      "Stack: good student (B average or better), driver-training course, multi-vehicle (parents' household), and telematics. The combined stack typically lands at 25-40% off the youthful-driver surcharge — pulling the multiplier down toward 1.4x.",
    eligibility_default: true,
    sample_age_band: "16-24",
  },
  {
    slug: "senior",
    label: "senior driver",
    label_title: "Senior Driver",
    plural: "senior drivers",
    multiplier: 0.92,
    intent:
      'queries like "{carrier} senior driver insurance {state}" + "best car insurance for seniors {state}" + "{carrier} insurance over 65 {state}"',
    risk_summary:
      "Drivers 65-74 with clean records pay ~8% LESS than the 35-44 baseline at most major carriers — accident frequency drops with age until ~75, when it begins to rise again. AARP Driver Safety completion (8-hour classroom or 5-hour online) qualifies for a state-mandated 5-10% mature-driver discount in 35+ states.",
    coverage_notes:
      "Verify the carrier honors a low-annual-mileage class for retirees (most do <7,500 mi/yr at a 15-22% discount). If a household member is a part-time caregiver who drives the senior's vehicle for errands, list them as an occasional driver — keeping them OFF the policy entirely can void claims involving them.",
    discount_tilt:
      "AARP/Hartford carriers (The Hartford, AAA in some markets) ship the deepest mature-driver discount stacks — typically 5-10% AARP affinity + 5-15% mature-driver course + 18-25% low-mileage retiree. Combined stack can reach 30-40% off baseline for a 65+ retired driver with <7,500 mi/yr.",
    eligibility_default: true,
    sample_age_band: "65+",
  },
  {
    slug: "sr22",
    label: "SR-22 high-risk",
    label_title: "SR-22 High-Risk",
    plural: "SR-22 drivers",
    multiplier: 1.55,
    intent:
      'queries like "{carrier} SR-22 insurance {state}" + "cheap SR-22 {state}" + "who writes SR-22 in {state}"',
    risk_summary:
      "SR-22 is a financial-responsibility certificate the carrier files with your state DMV — typically required after a DUI, license suspension, at-fault uninsured accident, or accumulated points. The filing itself costs $15-25, but the underlying high-risk classification adds a 30-50% surcharge that persists for 3-5 years (state-dependent).",
    coverage_notes:
      "The SR-22 must be maintained continuously — even a 1-day lapse triggers carrier notification to the DMV, which can re-suspend your license. If you cancel and re-shop carriers, request the new policy be active BEFORE you cancel the old one. The filing transfers with you only if you stay with the same carrier or arrange concurrent filings.",
    discount_tilt:
      "High-risk discount stacks are thin: defensive-driving course (5-10% in most states), telematics (10-20% if you can demonstrate clean driving on the new policy), pay-in-full (5-7%). The biggest savings come from comparison shopping — SR-22 rates between eligible carriers can vary 60-80% for the same risk profile.",
    eligibility_default: false,
    eligible_carriers: [
      "geico", "progressive", "state-farm", "allstate",
      "farmers", "nationwide", "liberty-mutual",
    ],
    sample_age_band: "any (SR-22 required)",
  },
  {
    slug: "low-mileage",
    label: "low-mileage retiree",
    label_title: "Low-Mileage Driver",
    plural: "low-mileage drivers",
    multiplier: 0.82,
    intent:
      'queries like "{carrier} low mileage insurance {state}" + "pay per mile insurance {state}" + "{carrier} insurance retiree {state}"',
    risk_summary:
      "Drivers under 7,500 miles/year qualify for a low-mileage rate class at most major carriers — typically 18-25% below the standard rate class. The category captures retirees, remote workers, and households with multiple vehicles where one is driven infrequently.",
    coverage_notes:
      "Carriers verify mileage at policy renewal — through odometer photos, telematics dongles, or self-report. Overstating mileage to qualify is policy fraud; understating real mileage and exceeding the band can invalidate claims. Re-evaluate your mileage class every annual renewal.",
    discount_tilt:
      "True pay-per-mile programs (Allstate Milewise, Nationwide SmartMiles, Progressive Snapshot per-mile mode) stack additional 10-25% on top of the standard low-mileage discount for drivers under 5,000 mi/yr. Liberty Mutual RightTrack telematics achieves similar discounts via the conservative-driving signal rather than pure mileage.",
    eligibility_default: true,
    sample_age_band: "any (<7,500 mi/yr)",
  },
];

export const DRIVER_PERSONAS_BY_SLUG = Object.fromEntries(
  DRIVER_PERSONAS.map((p) => [p.slug, p]),
);

/**
 * Iter-185.BS — Carrier × persona eligibility check. Returns `true` if
 * the carrier writes policies for this persona class. Critical for the
 * truthfulness mandate — SR-22 is restricted to 7 of 14 carriers, and
 * the page MUST NOT fabricate a premium estimate for an ineligible
 * pair. Ineligible pairs render the "carrier-does-not-write-X"
 * variant page with a redirect block to eligible alternatives.
 */
export function isCarrierEligibleForPersona(carrierSlug, personaSlug) {
  const persona = DRIVER_PERSONAS_BY_SLUG[personaSlug];
  if (!persona) return false;
  if (persona.eligibility_default) return true;
  return (persona.eligible_carriers || []).includes(carrierSlug);
}

/**
 * Iter-185.BS — Carrier × State × Persona premium estimate (eligible
 * pairs only). For ineligible (carrier, persona) pairs, returns null
 * — the page renders an ineligibility variant.
 */
export function estimateCarrierStatePersonaPremium(carrierSlug, stateSlug, personaSlug, overrides = null) {
  const base = estimateCarrierStatePremium(carrierSlug, stateSlug, overrides);
  if (!base) return null;
  const basePersona = DRIVER_PERSONAS_BY_SLUG[personaSlug];
  if (!basePersona) return null;
  if (!isCarrierEligibleForPersona(carrierSlug, personaSlug)) return null;
  const multiplier = overrides?.persona_multipliers?.[personaSlug] ?? basePersona.multiplier;
  const persona = multiplier === basePersona.multiplier
    ? basePersona
    : { ...basePersona, multiplier };
  const adjustedAnnual = Math.round((base.estimated_annual * multiplier) / 25) * 25;
  return {
    ...base,
    persona,
    estimated_annual: adjustedAnnual,
    estimated_monthly: Math.round(adjustedAnnual / 12),
    baseline_annual: base.estimated_annual,
    persona_delta_pct: Math.round((multiplier - 1) * 1000) / 10,
    vs_state_avg_pct:
      Math.round(((adjustedAnnual - base.state_avg) / base.state_avg) * 1000) / 10,
  };
}

/**
 * Iter-185.BS — Eligible cheaper alternatives for a (state, persona)
 * tuple. Powers the "Cheaper {persona} insurance alternatives in
 * {state}" block AND the "Who writes SR-22 in {state}" redirect block
 * on the ineligibility-variant page.
 *
 * Iter-185.BU — Override-aware via the `overrides` pass-through.
 */
export function altCarriersInStateForPersona(currentCarrierSlug, stateSlug, personaSlug, overrides = null) {
  const rows = CARRIERS.filter(
    (c) => c.slug !== currentCarrierSlug && isCarrierEligibleForPersona(c.slug, personaSlug),
  ).map((c) => {
    const est = estimateCarrierStatePersonaPremium(c.slug, stateSlug, personaSlug, overrides);
    return { carrier: c, est: est?.estimated_annual ?? Infinity };
  });
  return rows.sort((a, b) => a.est - b.est).slice(0, 3);
}

export const carrierStatePersonaPath = (carrierSlug, stateSlug, personaSlug) =>
  `/auto-insurance/${stateSlug}/${carrierSlug}/for/${personaSlug}`;

/**
 * Iter-185.BS — Enumerate every (carrier, state, persona) tuple in
 * the matrix. 14 carriers × 51 states × 4 personas = 2,856 unique
 * long-tail pages (7 eligible carriers × 51 states for SR-22 +
 * 14 × 51 for each of the other 3 personas = 357 + 2,142 = 2,499
 * eligible-pair URLs + 357 ineligibility-variant URLs = 2,856 total).
 * All 2,856 are sitemap-included — ineligibility variants are
 * intentionally indexable because they directly serve search intent
 * ("USAA SR-22 California" → honest "USAA does not write SR-22, here
 * are 3 who do" page outranks competitors).
 */
export function allCarrierStatePersonaTuples() {
  const tuples = [];
  for (const c of CARRIERS) {
    for (const s of STATES) {
      for (const p of DRIVER_PERSONAS) {
        tuples.push({
          carrier: c,
          state: s,
          persona: p,
          eligible: isCarrierEligibleForPersona(c.slug, p.slug),
          path: carrierStatePersonaPath(c.slug, s.slug, p.slug),
        });
      }
    }
  }
  return tuples;
}
